/**
 * growthIntel/tracker — first-party client tracking.
 *
 * Sends click/start interaction events to the `growth-intel-track` edge
 * function. These are interaction signals ONLY — never counted as conversions.
 * No PII, no free text, no field values, no filenames are ever sent.
 *
 * Also owns the cross-session first-touch attribution ('cendien_ft', 90-day
 * expiry, never overwritten until it expires) plus the current session touch.
 *
 * Reserved test key hygiene: `admin_visit` marks an internal visitor so their
 * traffic can be excluded from reports.
 */

import { getSiteEnv, isProduction } from './env';
import { canStoreAnonId } from './consent';

const SUPABASE_URL = import.meta.env.VITE_PUBLIC_SUPABASE_URL ?? '';
const SUPABASE_ANON_KEY = import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY ?? '';

const FT_KEY = 'cendien_ft';
const ST_KEY = 'cendien_st';
const ANON_KEY = 'cendien_anon';
const ADMIN_VISIT_KEY = 'cendien_admin_visit';
const TEST_KEY = 'cendien_gi_test';
const FT_TTL_MS = 90 * 24 * 60 * 60 * 1000;

const UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'] as const;
type UtmKey = typeof UTM_KEYS[number];

/** The only client event names the tracker will send. */
export type GiClientEvent =
  | 'cta_click' | 'tel_click' | 'mailto_click' | 'outbound_click'
  | 'contact_form_start' | 'contact_form_client_error'
  | 'chat_start' | 'resume_upload_start' | 'rfp_upload_start'
  | 'upload_client_error' | 'guide_download_click' | 'newsletter_start';

/** Only these props keys are ever forwarded. */
const ALLOWED_PROP_KEYS = ['label', 'target_path', 'host', 'validation', 'upload_kind', 'category'] as const;

function safeGet(store: Storage, key: string): string | null {
  try { return store.getItem(key); } catch { return null; }
}
function safeSet(store: Storage, key: string, value: string): void {
  try { store.setItem(key, value); } catch { /* ignore */ }
}

function uuid(): string {
  try { if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID(); } catch { /* ignore */ }
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

function getSessionId(): string {
  const KEY = 'cendien_gi_session';
  const EXP = 'cendien_gi_session_exp';
  const now = Date.now();
  let sid = safeGet(sessionStorage, KEY);
  const exp = Number(safeGet(sessionStorage, EXP) || '0');
  if (!sid || now > exp) { sid = uuid(); safeSet(sessionStorage, KEY, sid); }
  safeSet(sessionStorage, EXP, String(now + 30 * 60 * 1000));
  return sid;
}

/** Random first-party id. Stored only when consent allows. */
export function getAnonId(): string {
  if (!canStoreAnonId()) return '';
  let id = safeGet(localStorage, ANON_KEY);
  if (!id) { id = uuid(); safeSet(localStorage, ANON_KEY, id); }
  return id;
}

export interface Utm { source: string; medium: string; campaign: string; term: string; content: string; }

function readUtm(): Utm {
  const out: Utm = { source: '', medium: '', campaign: '', term: '', content: '' };
  try {
    const params = new URLSearchParams(window.location.search);
    const map: Record<UtmKey, keyof Utm> = {
      utm_source: 'source', utm_medium: 'medium', utm_campaign: 'campaign', utm_term: 'term', utm_content: 'content',
    };
    UTM_KEYS.forEach((k) => {
      const v = params.get(k);
      if (v) out[map[k]] = v.trim().slice(0, 120);
    });
  } catch { /* ignore */ }
  return out;
}

/** True when the CURRENT URL's UTM set is malformed. */
export function isCurrentUtmMalformed(): boolean {
  return utmMalformed(readUtm());
}

/** True when a UTM set is malformed (uppercase, whitespace, or missing medium). */
function utmMalformed(u: Utm): boolean {
  const values = [u.source, u.medium, u.campaign, u.term, u.content];
  const hasAny = values.some((v) => v.length > 0);
  if (!hasAny) return false;
  if (!u.medium) return true;
  const bad = values.some((v) => v && (v !== v.toLowerCase() || /\s/.test(v)));
  return bad;
}

/** Static site contact constants — safe to record on tel/mailto clicks. */
export const SITE_PHONE_LABEL = 'site-phone';
export const SITE_EMAIL_LABEL = 'site-email';

/** Cross-session first touch. Persists 90 days, never overwritten until expiry. */
export function getFirstTouch(): Record<string, unknown> | null {
  try {
    const raw = safeGet(localStorage, FT_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as Record<string, unknown>;
      const ts = Number(parsed?.ts || 0);
      if (ts && Date.now() - ts < FT_TTL_MS) return parsed;
      localStorage.removeItem(FT_KEY);
    }
  } catch { /* ignore */ }
  // (Re)create from the current URL only when there is something to record.
  const utm = readUtm();
  const referrerHost = referrerHostname();
  const hasUtm = [utm.source, utm.medium, utm.campaign, utm.term, utm.content].some(Boolean);
  if (!hasUtm && !referrerHost) return null;
  const ft: Record<string, unknown> = {
    source: utm.source, medium: utm.medium, campaign: utm.campaign, term: utm.term, content: utm.content,
    referrer_host: referrerHost, landing_path: window.location.pathname, ts: Date.now(), malformed: utmMalformed(utm),
  };
  safeSet(localStorage, FT_KEY, JSON.stringify(ft));
  return ft;
}

/** Current-session touch (updated per navigation). */
export function getSessionTouch(): Record<string, unknown> {
  const utm = readUtm();
  const hasUtm = [utm.source, utm.medium, utm.campaign, utm.term, utm.content].some(Boolean);
  if (hasUtm) {
    const st: Record<string, unknown> = {
      source: utm.source, medium: utm.medium, campaign: utm.campaign, term: utm.term, content: utm.content,
      landing_path: window.location.pathname, referrer_host: referrerHostname(), ts: Date.now(),
    };
    safeSet(sessionStorage, ST_KEY, JSON.stringify(st));
    return st;
  }
  try {
    const raw = safeGet(sessionStorage, ST_KEY);
    if (raw) return JSON.parse(raw) as Record<string, unknown>;
  } catch { /* ignore */ }
  return { landing_path: window.location.pathname, referrer_host: referrerHostname(), ts: Date.now() };
}

function referrerHostname(): string {
  try {
    if (!document.referrer) return '';
    return new URL(document.referrer).hostname.toLowerCase();
  } catch { return ''; }
}

export function getDeviceType(): string {
  const ua = navigator.userAgent.toLowerCase();
  if (/tablet|ipad|playbook|silk|(android(?!.*mobi))/.test(ua)) return 'tablet';
  if (/mobile|android|iphone|ipod|blackberry|windows phone|opera mini|iemobile/.test(ua)) return 'mobile';
  return 'desktop';
}

/** Marks the browser as an internal visitor (set when /admin is visited). */
export function markInternalVisitor(): void {
  safeSet(localStorage, ADMIN_VISIT_KEY, '1');
}
export function isInternalVisitor(): boolean {
  return safeGet(localStorage, ADMIN_VISIT_KEY) === '1';
}

/** Test mode — sticky for the session via ?gi_test=1. */
export function initTestFlag(): void {
  try {
    const params = new URLSearchParams(window.location.search);
    if (params.get('gi_test') === '1') safeSet(sessionStorage, TEST_KEY, '1');
  } catch { /* ignore */ }
}
export function isTestVisitor(): boolean {
  return safeGet(sessionStorage, TEST_KEY) === '1';
}

function sanitizeProps(props: Record<string, unknown> | undefined): Record<string, string> {
  const out: Record<string, string> = {};
  if (!props) return out;
  ALLOWED_PROP_KEYS.forEach((k) => {
    const v = props[k];
    if (typeof v === 'string' && v.trim()) out[k] = v.trim().slice(0, 120);
    else if (typeof v === 'number' || typeof v === 'boolean') out[k] = String(v);
  });
  return out;
}

function pageType(path: string): string {
  if (path === '/') return 'home';
  if (path.startsWith('/services')) return 'service';
  if (path.startsWith('/industries')) return 'industry';
  if (path.startsWith('/insights')) return 'insight';
  if (path.startsWith('/newsroom')) return 'newsroom';
  if (path.startsWith('/careers')) return 'careers';
  if (path.startsWith('/resources')) return 'resource';
  if (path.startsWith('/contact')) return 'contact';
  return 'other';
}

/**
 * Sends one client interaction event. No-ops unless on production, and only
 * ever forwards allow-listed, non-PII data.
 */
export function trackGiEvent(event: GiClientEvent, props?: Record<string, unknown>): void {
  try {
    if (!SUPABASE_URL || !isProduction()) return;
    const path = window.location.pathname;
    const payload = {
      event_name: event,
      page_path: path,
      page_type: pageType(path),
      session_id: getSessionId(),
      anon_id: getAnonId(),
      referrer_host: referrerHostname(),
      device_type: getDeviceType(),
      environment: getSiteEnv(),
      is_internal: isInternalVisitor(),
      is_test: isTestVisitor(),
      utm: getSessionTouch(),
      first_touch: getFirstTouch(),
      props: sanitizeProps(props),
    };
    fetch(`${SUPABASE_URL}/functions/v1/growth-intel-track`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', apikey: SUPABASE_ANON_KEY, Authorization: `Bearer ${SUPABASE_ANON_KEY}` },
      body: JSON.stringify(payload),
    }).catch(() => { /* never break the app */ });
  } catch { /* ignore */ }
}

/** Attribution bundle to attach to form/chat/upload submissions (stored in our DB only). */
export function buildAttributionPayload(): {
  utm: Record<string, string>;
  first_touch: Record<string, unknown> | null;
  session_touch: Record<string, unknown>;
  anon_id: string;
} {
  const st = getSessionTouch();
  const utm: Record<string, string> = {};
  (['source', 'medium', 'campaign', 'term', 'content'] as const).forEach((k) => {
    const v = st[k];
    if (typeof v === 'string' && v) utm[`utm_${k}`] = v;
  });
  return {
    utm,
    first_touch: getFirstTouch(),
    session_touch: st,
    anon_id: getAnonId(),
  };
}

/** Canonical attribution bundle attached to EVERY public conversion submission. */
export interface ConversionAttribution {
  page: string;
  utm: Record<string, string>;
  session_touch: Record<string, unknown> | null;
  first_touch: Record<string, unknown> | null;
  landing_path: string | null;
  referrer_host: string | null;
  session_id: string | null;
  anon_id: string | null;
  malformed: boolean;
  captured_at: string;
}

/**
 * One bundle for all conversion flows: current session touch, the cross-session
 * first touch ('cendien_ft'), landing page, referrer host and the malformed-UTM
 * flag. No PII — stored in our own database only, never sent to a provider.
 */
export function getConversionAttribution(): ConversionAttribution {
  const st = getSessionTouch();
  const ft = getFirstTouch();
  const utm: Record<string, string> = {};
  (['source', 'medium', 'campaign', 'term', 'content'] as const).forEach((k) => {
    const v = st[k];
    if (typeof v === 'string' && v) utm[`utm_${k}`] = v;
  });
  const stLanding = typeof st.landing_path === 'string' ? st.landing_path : '';
  const ftLanding = ft && typeof ft.landing_path === 'string' ? (ft.landing_path as string) : '';
  const stRef = typeof st.referrer_host === 'string' ? st.referrer_host : '';
  const ftRef = ft && typeof ft.referrer_host === 'string' ? (ft.referrer_host as string) : '';
  let path = '/';
  try { path = window.location.pathname + window.location.search; } catch { /* ignore */ }
  const landing = stLanding || ftLanding || (typeof window !== 'undefined' ? window.location.pathname : '/');
  const refHost = stRef || ftRef || referrerHostname();
  return {
    page: path,
    utm,
    session_touch: st,
    first_touch: ft,
    landing_path: landing || null,
    referrer_host: refHost || null,
    session_id: getSessionId(),
    anon_id: getAnonId() || null,
    malformed: isCurrentUtmMalformed(),
    captured_at: new Date().toISOString(),
  };
}